import { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { CircleAlert, Info } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Separator,
} from '@predoc/cow';
import { DatePickerInput } from '@/components/DatePickerInput';
import { TreeView } from '@/components/Treeview';
import {
  useGetDownloadAvailableParameters,
  useSubmitDownloadRequest,
} from '@/feature/downloads/hooks/downloadsAPI';
import { DownloadMedicalCategory } from '@/feature/downloads/types';
import {
  type DocumentNode,
  type DocumentNodeData,
  mapDocumentsToNodes,
  mapSelectedIdsToDownloadPayload,
} from '../../utils/mapDownloadUtils';

const DocumentLabel = ({ node }: { node: DocumentNode }) => (
  <>
    {node.label}
    {!!node.count && (
      <span className="text-muted-foreground ml-1">({node.count})</span>
    )}
  </>
);
interface DownloadDocumentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  patientId: string;
}

export const DownloadDocumentsModal = ({
  isOpen,
  onClose,
  patientId,
}: DownloadDocumentsModalProps) => {
  const [serviceStartDate, setServiceStartDate] = useState<Date | undefined>();
  const [serviceEndDate, setServiceEndDate] = useState<Date | undefined>(
    new Date(),
  );
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<string[]>([]);

  const hasDateRangeError =
    !!serviceStartDate && !!serviceEndDate && serviceStartDate > serviceEndDate;

  const { data, isLoading, status } = useGetDownloadAvailableParameters(
    patientId,
    serviceStartDate,
    serviceEndDate,
    { enabled: isOpen && !hasDateRangeError },
  );

  const { mutate: submitDownload, isPending } = useSubmitDownloadRequest();

  const hasNoData =
    status === 'success' &&
    !isLoading &&
    !data?.medical?.categories?.length &&
    data?.nonMedical?.count === 0;

  const documents = useMemo(() => mapDocumentsToNodes(data), [data]);

  const handleClose = () => {
    setServiceStartDate(undefined);
    setServiceEndDate(new Date());
    setSelectedDocumentIds([]);
    onClose();
  };

  const handleDownload = () => {
    const { nonMedical, medical } =
      mapSelectedIdsToDownloadPayload(selectedDocumentIds);
    submitDownload(
      {
        patientId,
        startDate: serviceStartDate
          ? format(serviceStartDate, 'yyyy-MM-dd')
          : undefined,
        endDate: serviceEndDate
          ? format(serviceEndDate, 'yyyy-MM-dd')
          : undefined,
        nonMedical,
        medical: medical as {
          category: DownloadMedicalCategory;
          subcategories?: string[] | undefined;
        }[],
      },
      { onSuccess: handleClose },
    );
  };

  const isDownloadDisabled =
    selectedDocumentIds.length === 0 ||
    hasDateRangeError ||
    isLoading ||
    isPending;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open: boolean) => !open && handleClose()}
    >
      <DialogContent className="sm:max-w-[740px]">
        <DialogHeader>
          <DialogTitle>Download...</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <Alert variant="accent">
            <Info className="size-4" />
            <AlertDescription>
              Select a service date range for the patient.
            </AlertDescription>
          </Alert>

          <div className="space-y-1.5">
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">
                  Service Start Date
                </label>
                <DatePickerInput
                  onChange={setServiceStartDate}
                  value={serviceStartDate}
                  placeholder="The beginning of time"
                  disableDates="future"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Service End Date</label>
                <DatePickerInput
                  onChange={setServiceEndDate}
                  value={serviceEndDate}
                  placeholder="MM/DD/YYYY"
                  disableDates="future"
                />
              </div>
            </div>
            {hasDateRangeError && (
              <p className="text-destructive text-sm">
                Start date must be on or before end date.
              </p>
            )}
          </div>

          <div className="space-y-2.5">
            <h4 className="text-base font-bold">Documents</h4>
            <p className="text-muted-foreground text-sm">
              Select from available record and document types to create your
              download.
            </p>
          </div>

          {hasNoData ? (
            <Alert variant="destructive">
              <CircleAlert className="size-4" />
              <AlertTitle>
                No data or documents available for this service date range
              </AlertTitle>
            </Alert>
          ) : null}

          <div className="rounded-lg border border-neutral-300">
            {documents.map((group, index) => (
              <div key={group.id}>
                {index > 0 && <Separator />}
                <div className="p-2.5">
                  <TreeView<DocumentNodeData>
                    data={[group]}
                    selectedIds={selectedDocumentIds}
                    onSelectionChange={setSelectedDocumentIds}
                    defaultExpandedIds={['medical-records']}
                    renderLabel={(node) => <DocumentLabel node={node} />}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleDownload} disabled={isDownloadDisabled}>
            Download
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
